// src/utils/laborBilling.js
// LABORBILL VERIFIER: laborbill-one-rule-6c2a9e47
//
// THE ONE RULE for tracked labour on an invoice — used by BOTH the invoice builder
// (CrmApp.createNewInvoiceFromSource) and the job editor's Tracked Labor readout
// (JobFormModal), so what the editor says the next invoice will charge is exactly what
// the invoice charges. Before this, the editor showed "Billed $X" from hours × bill-out
// rate with no switch check and no already-invoiced check, while the invoice also needed
// company "Track labor time" ON — so a job could promise $0.80 and invoice $0 (founder
// step 4, 2026-10-04).
//
// FOUNDER DECISION (Marcus, 2026-10-04): the JOB's own labour-billing choice wins in both
// directions —
//   job "Bill labor on this job"  (true)   → bills, even if company tracking/billing is off
//   job "Do not bill labor"       (false)  → never bills
//   job "Use org default"         (null)   → the company default, which is ON only when
//                                            "Track labor time" AND "Bill tracked labor to
//                                            customers by default" are both on (the settings
//                                            screen only offers the second when the first is on)
// "Track labor time" never stopped clocking — hours accrue either way — so a per-job
// override always has hours to bill.
//
// The line maths below is the code that lived inline in CrmApp (r5-b2b-labor-inject-2a7c9f18),
// moved verbatim: clocked rows only; delta = accrued − highest hours already billed on this
// job's invoices (immutable stamps), rounded to 2dp; nothing new → nothing; no bill-out rate →
// skipped (and the stamp is not advanced, so it bills later). Bill-out rate = the person's
// billableRate — never the labour COST rate (three-rate model).

// job.billLaborToCustomer (true / false / null|undefined) + org automationSettings → boolean.
export const resolveBillLabor = (jobSetting, automationSettings) => {
    if (jobSetting === true) return true;
    if (jobSetting === false) return false;
    const a = automationSettings || {};
    return a.trackLaborTime === true && a.billLaborToCustomer === true;
};

// Highest laborBilledThroughHours per worker across a job's existing invoices' labour lines.
export const priorBilledFromInvoices = (invoices) => {
    const priorMax = {};
    (Array.isArray(invoices) ? invoices : []).forEach((inv) => {
        ((inv && inv.lineItems) || []).forEach((li) => {
            if (li.source === 'labor' && li.laborUserId) {
                const billed = Number(li.laborBilledThroughHours) || 0;
                if (billed > (priorMax[li.laborUserId] || 0)) priorMax[li.laborUserId] = billed;
            }
        });
    });
    return priorMax;
};

// Per-person status + the invoice lines, from one set of inputs.
//   laborEntries        job.jobCosting.laborEntries
//   billableRateByUid   { uid: billableRate }
//   nameByUid           { uid: name } (fallback when the row has no name)
//   priorBilledByUid    priorBilledFromInvoices(...) output
//   billLabor           resolveBillLabor(...) output
// status per uid: 'billed' | 'alreadyInvoiced' | 'noRate' | 'notBilledSwitchOff' | 'notClocked'
export const computeLaborBilling = ({ laborEntries, billableRateByUid, nameByUid, priorBilledByUid, billLabor }) => {
    const lines = [];
    const byUser = {};
    (Array.isArray(laborEntries) ? laborEntries : []).forEach((entry) => {
        const uid = entry && entry.userId;
        if (!uid) return;
        if (entry.source !== 'clocked') { byUser[uid] = { status: 'notClocked', delta: 0, rate: 0, amount: 0 }; return; }
        const accrued = Number(entry.hours) || 0;
        const delta = Number((accrued - ((priorBilledByUid || {})[uid] || 0)).toFixed(2));
        const rate = Number((billableRateByUid || {})[uid]) || 0;
        if (!billLabor) { byUser[uid] = { status: 'notBilledSwitchOff', delta: Math.max(delta, 0), rate, amount: 0 }; return; }
        if (delta <= 0) { byUser[uid] = { status: 'alreadyInvoiced', delta: 0, rate, amount: 0 }; return; }
        if (rate <= 0) { byUser[uid] = { status: 'noRate', delta, rate: 0, amount: 0 }; return; }
        const who = entry.name || (nameByUid || {})[uid] || 'Employee';
        lines.push({
            name: 'Labor',
            description: `${who} — ${delta}h @ $${rate}/hr`,
            quantity: delta,
            price: rate,
            type: 'Service',
            taxCodeId: '',
            taxExempt: false,
            taxExemptionNumber: '',
            serviceId: '',
            itemId: '',
            addToCatalog: false,
            source: 'labor',
            laborUserId: uid,
            laborBilledThroughHours: accrued,
        });
        byUser[uid] = { status: 'billed', delta, rate, amount: delta * rate };
    });
    return { lines, byUser };
};
